import React from "react"
import {
  ProviderType,
  type Transaction,
  useReadBooksClosedDateQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import useSession from "@digits-shared/hooks/useSession"
import type FrontendSession from "src/frontend/session"

/*
  CONSTANTS
*/

const VALID = { valid: true, reason: "" }
const VALID_PROVIDER_TYPES = new Set([
  ProviderType.CSVImport,
  ProviderType.Connect,
  ProviderType.Manual,
  ProviderType.QuickBooks,
  ProviderType.DigitsPay,
  ProviderType.DigitsInvoicing,
])

/*
  INTERFACES
*/

interface Validation {
  valid: boolean
  reason: string
}

/*
  HOOKS
*/

// Confirms individual transactions are valid for matching
export const useTransactionCanBeMatched = (transaction: Transaction | Transaction[]) => {
  const closedTimestamp = useGetClosedTimestamp()

  return React.useMemo(() => {
    const transactions = Array.isArray(transaction) ? transaction : [transaction]

    switch (true) {
      case hasTransactionsInClosedPeriod(transactions, closedTimestamp):
        return {
          valid: false,
          reason: "Transactions in closed period cannot be matched",
        }
      case hasTransactionsWithMoreThanTwoLines(transactions):
        return {
          valid: false,
          reason: "Transactions with more than 2 lines can not be matched",
        }
      case hasInvalidProviderTypes(transactions):
        return {
          valid: false,
          reason: "Can only match transactions imported via connect, csv, or manually entered",
        }
      case hasMatchedTransactions(transactions):
        return {
          valid: false,
          reason: "Transactions are already matched",
        }
      default:
        return VALID
    }
  }, [transaction, closedTimestamp])
}

// Confirms transactions are valid to be matched with one another
export const useTransactionsCanBeMatched = (transactions: Transaction[]) => {
  const filteredTransactions = React.useMemo(
    () => filterUniqueLedgerTransactions(transactions),
    [transactions]
  )

  const withUniqueTransactionCount = React.useCallback(
    (v: Validation) => ({
      uniqueTransactionCount: filteredTransactions.length,
      ...v,
    }),
    [filteredTransactions]
  )

  // Check individual transaction validity
  const { valid: transactionLinesValid, reason: transactionLineValidReason } =
    useTransactionCanBeMatched(filteredTransactions)

  return React.useMemo(() => {
    if (!transactionLinesValid) {
      return withUniqueTransactionCount({
        valid: transactionLinesValid,
        reason: transactionLineValidReason,
      })
    }

    // Check lines against each other
    switch (true) {
      case hasUnderTwoTransactions(filteredTransactions):
        return withUniqueTransactionCount({
          valid: false,
          reason: "Two distinct transactions are required to match",
        })
      case hasInvalidTransactionCount(filteredTransactions):
        return withUniqueTransactionCount({
          valid: false,
          reason: "Only two transactions can be matched to one another",
        })
      case hasDifferentAmount(filteredTransactions):
        return withUniqueTransactionCount({
          valid: false,
          reason: "Transactions with different amounts can not be matched to one another",
        })
      case hasSameSourceAccount(filteredTransactions):
        return withUniqueTransactionCount({
          valid: false,
          reason: "Transactions with the same source account can not be matched to one another",
        })
      default:
        return withUniqueTransactionCount(VALID)
    }
  }, [
    transactionLinesValid,
    withUniqueTransactionCount,
    transactionLineValidReason,
    filteredTransactions,
  ])
}

function hasUnderTwoTransactions(transactions: Transaction[]) {
  return transactions.length < 2
}

function hasInvalidTransactionCount(transactions: Transaction[]) {
  return transactions.length !== 2
}

function hasTransactionsWithMoreThanTwoLines(transactions: Transaction[]) {
  return transactions.some(({ splitCategory }) => !splitCategory)
}

function hasDifferentAmount(transactions: Transaction[]) {
  if (transactions.length === 0) {
    return false
  }

  // Will never fallback to zero value because of the previous check
  const amount = transactions[0]?.moneyFlow.value || monetaryValueHelper.buildZeroMonetaryValue()

  return transactions.reduce(
    (acc, { moneyFlow: { value } }) => acc || !monetaryValueHelper.equals(value, amount),
    false
  )
}
function hasSameSourceAccount(transactions: Transaction[]) {
  const { same } = transactions.reduce(
    (acc, { sources }) => {
      // If one of the prior transactions has the same source account, we can skip the rest
      if (acc.same) {
        return acc
      }

      const accountIds = new Set(
        sources.map(({ account }) => account?.sourceAccountId).filter(defined)
      )
      // If there are no source account ids for the current transaction, we can skip the rest
      if (accountIds.size === 0) {
        return acc
      }

      // If there are no source account ids from prior transactions, we can set it to the current transaction ids
      if (acc.sourceAccounts.size === 0) {
        return { sourceAccounts: new Set(accountIds), same: false }
      }

      return {
        sourceAccounts: acc.sourceAccounts.union(new Set(accountIds)),
        same: accountIds.intersection(acc.sourceAccounts).size > 0,
      }
    },
    {
      sourceAccounts: new Set<string>(),
      same: false,
    }
  )

  return same
}

function hasTransactionsInClosedPeriod(
  transactions: Transaction[],
  closedTimestamp?: number | null
) {
  return (
    closedTimestamp && transactions.some((transaction) => transaction.timestamp <= closedTimestamp)
  )
}

function useGetClosedTimestamp() {
  const { currentLegalEntityId } = useSession<FrontendSession>()
  const { data } = useReadBooksClosedDateQuery({
    variables: {
      legalEntityId: currentLegalEntityId,
    },
  })

  return React.useMemo(() => data?.readBooksClosedDate.latestBooksClosedDateFact, [data])
}

function hasMatchedTransactions(transactions: Transaction[]) {
  return transactions.some((transaction) => transaction.matchedLedgerTransactionIds.length > 0)
}

const filterUniqueLedgerTransactions = (transactions: Transaction[]): Transaction[] => {
  const ledgerIds = new Set<string>()

  return transactions
    .sort((a, b) => (a.factId || "").localeCompare(b.factId || ""))
    .filter((transaction) => {
      if (ledgerIds.has(transaction.ledgerTransactionId)) {
        return false
      }

      ledgerIds.add(transaction.ledgerTransactionId)
      return true
    })
}

// Connect, CSV, Manual are the valid provider types
function hasInvalidProviderTypes(transactions: Transaction[]) {
  return transactions.some(({ providerTypes }) =>
    providerTypes.some((type) => !VALID_PROVIDER_TYPES.has(type))
  )
}
